import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { rateLimitService } from '../../services/rateLimit';
import { RateLimitRuleEditor } from '../../components/ratelimit/RateLimitRuleEditor';
import { IpWhitelistManager } from '../../components/ratelimit/IpWhitelistManager';
import { RateLimitAnalytics } from '../../components/ratelimit/RateLimitAnalytics';
import type { AdminRateLimitRule, AdminIpWhitelistEntry, AdminRateLimitAnalytics } from '../../types/admin';

export const RateLimitSettings: React.FC = () => {
  const { t } = useTranslation('admin');
  const [rules, setRules] = useState<AdminRateLimitRule[]>([]);
  const [whitelist, setWhitelist] = useState<AdminIpWhitelistEntry[]>([]);
  const [analytics, setAnalytics] = useState<AdminRateLimitAnalytics | null>(null);
  const [selectedRule, setSelectedRule] = useState<AdminRateLimitRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testForm, setTestForm] = useState({
    ip: '',
    api_key: '',
    campaign_id: '',
    account_id: '',
    plan_id: '',
  });
  const [testResult, setTestResult] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [ruleData, whitelistData, analyticsData] = await Promise.all([
        rateLimitService.listRules({ per_page: 100 }),
        rateLimitService.listWhitelist({ per_page: 100 }),
        rateLimitService.getAnalytics(),
      ]);

      setRules(ruleData.data ?? []);
      setWhitelist(whitelistData.data ?? []);
      setAnalytics(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.loading_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const ruleColumns = useMemo(() => ([
    { key: 'name', header: t('rate_limits.rules.table.name') },
    { key: 'scope', header: t('rate_limits.rules.table.scope') },
    { key: 'scope_id', header: t('rate_limits.rules.table.scope_id'), render: (value: AdminRateLimitRule['scope_id']) => value ?? '-' },
    { key: 'scope_value', header: t('rate_limits.rules.table.scope_value'), render: (value: AdminRateLimitRule['scope_value']) => value ?? '-' },
    { key: 'requests_per_minute', header: t('rate_limits.rules.table.rpm'), render: (value: AdminRateLimitRule['requests_per_minute']) => value ?? '-' },
    { key: 'active', header: t('rate_limits.rules.table.active'), render: (value: AdminRateLimitRule['active']) => (value ? t('rate_limits.rules.active') : t('rate_limits.rules.inactive')) },
  ]), [t]);

  const handleSaveRule = async (payload: Partial<AdminRateLimitRule>) => {
    try {
      setError(null);
      if (selectedRule?.id) {
        await rateLimitService.updateRule(selectedRule.id, payload);
      } else {
        await rateLimitService.createRule(payload);
      }
      await fetchData();
      setSelectedRule(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.rules.save_error'));
    }
  };

  const handleDeleteRule = async (rule: AdminRateLimitRule) => {
    try {
      setError(null);
      await rateLimitService.deleteRule(rule.id);
      await fetchData();
      if (selectedRule?.id === rule.id) {
        setSelectedRule(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.rules.delete_error'));
    }
  };

  const handleAddWhitelist = async (payload: Partial<AdminIpWhitelistEntry>) => {
    try {
      setError(null);
      await rateLimitService.addWhitelist(payload);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.whitelist.add_error'));
    }
  };

  const handleDeleteWhitelist = async (entry: AdminIpWhitelistEntry) => {
    try {
      setError(null);
      await rateLimitService.deleteWhitelist(entry.id);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.whitelist.delete_error'));
    }
  };

  const handleEmergencyToggle = async () => {
    try {
      const enabled = !(analytics?.overview.emergency_mode ?? false);
      await rateLimitService.setEmergencyMode({ enabled });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.emergency.error'));
    }
  };

  const handleTest = async () => {
    try {
      setError(null);
      const result = await rateLimitService.testRule({
        ip: testForm.ip || undefined,
        api_key: testForm.api_key || undefined,
        campaign_id: testForm.campaign_id ? Number(testForm.campaign_id) : undefined,
        account_id: testForm.account_id ? Number(testForm.account_id) : undefined,
        plan_id: testForm.plan_id ? Number(testForm.plan_id) : undefined,
      });
      setTestResult(JSON.stringify(result.resolution, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rate_limits.test.error'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('rate_limits.title')}</h1>
          <p className="text-sm text-gray-600">{t('rate_limits.subtitle')}</p>
        </div>
        <Button
          variant={analytics?.overview.emergency_mode ? 'danger' : 'secondary'}
          onClick={handleEmergencyToggle}
          className="w-full sm:w-auto"
        >
          {analytics?.overview.emergency_mode ? t('rate_limits.emergency.disable') : t('rate_limits.emergency.enable')}
        </Button>
      </div>

      {error && (
        <Alert variant="error" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card>
          <p className="text-sm text-gray-500">{t('rate_limits.loading')}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="space-y-4">
              <Card>
                <h2 className="text-lg font-semibold mb-3">{t('rate_limits.rules.title')}</h2>
                <Table columns={ruleColumns} data={rules} onRowClick={setSelectedRule} />
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-3">{t('rate_limits.test.title')}</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Input
                    placeholder={t('rate_limits.test.ip')}
                    value={testForm.ip}
                    onChange={(event) => setTestForm((prev) => ({ ...prev, ip: event.target.value }))}
                  />
                  <Input
                    placeholder={t('rate_limits.test.api_key')}
                    value={testForm.api_key}
                    onChange={(event) => setTestForm((prev) => ({ ...prev, api_key: event.target.value }))}
                  />
                  <Input
                    placeholder={t('rate_limits.test.campaign_id')}
                    value={testForm.campaign_id}
                    onChange={(event) => setTestForm((prev) => ({ ...prev, campaign_id: event.target.value }))}
                  />
                  <Input
                    placeholder={t('rate_limits.test.account_id')}
                    value={testForm.account_id}
                    onChange={(event) => setTestForm((prev) => ({ ...prev, account_id: event.target.value }))}
                  />
                  <Input
                    placeholder={t('rate_limits.test.plan_id')}
                    value={testForm.plan_id}
                    onChange={(event) => setTestForm((prev) => ({ ...prev, plan_id: event.target.value }))}
                  />
                </div>
                <div className="mt-3 flex justify-end">
                  <Button variant="secondary" onClick={handleTest}>
                    {t('rate_limits.test.run')}
                  </Button>
                </div>
                {testResult && (
                  <pre className="mt-3 rounded-lg bg-gray-900 text-gray-100 p-3 text-xs overflow-auto">
                    {testResult}
                  </pre>
                )}
              </Card>
            </div>

            <RateLimitRuleEditor
              rule={selectedRule}
              onSave={handleSaveRule}
              onDelete={handleDeleteRule}
              onReset={() => setSelectedRule(null)}
            />
          </div>

          <RateLimitAnalytics analytics={analytics} />

          <IpWhitelistManager entries={whitelist} onAdd={handleAddWhitelist} onDelete={handleDeleteWhitelist} />
        </>
      )}
    </div>
  );
};

export default RateLimitSettings;
